import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTalentByIdAsync,
  fetchTalentsAsync,
} from "../../features/talents/talentsSlice";
import axios from "axios";

import {
  Grid,
  Box,
  SelectChangeEvent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  ButtonGroup,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { TableView } from "@mui/icons-material";
import { Talent } from "../../features/talents/talentsTypes";
import { AppDispatch, RootState } from "../../store";
import TalentCard from "./TalentCard";
import TalentTable from "./TalentTableList";
import LoadingSpinner from "../shared/LoadingSpinner";
import FilterBar from "../FilterBar";
import useCustomMediaQuery from "../../hooks/useMediaQuery";
import GridViewIcon from "@mui/icons-material/GridView";
import TalentDetailDrawer from "./TalentDetails";
import { fetchPositions } from "../../features/positionsSlice";
import { API_URL } from "../../config/api";
import { useSnackbar } from "../SnackbarProvider";
import { useTranslation } from "react-i18next";

const TalentList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { talents, loading } = useSelector((state: RootState) => state.talents);
  const { positions, loading: positionsLoading } = useSelector(
    (state: RootState) => state.positions
  );
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { selectedTalent: talent } = useSelector(
    (state: RootState) => state.talents
  );
  const { openSnackbar } = useSnackbar();
  const companyId =
    user?.accountType === "company"
      ? (user.companyId || user.id || user.userId || "")
      : "";

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [titleFilter, setTitleFilter] = useState<string>("");
  const [pointRange, setPointRange] = useState<number[]>([0, 100]);
  const [sortOption, setSortOption] = useState<string>("name");
  const [selectedTalent, setSelectedTalent] = useState<Talent | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "card">("card");
  const [selectedPositionId, setSelectedPositionId] = useState<string>("");
  const [batchRunning, setBatchRunning] = useState(false);
  const [requestInterviewLoading, setRequestInterviewLoading] = useState(false);
  const [hideLoading, setHideLoading] = useState(false);
  const [showHiddenCandidates, setShowHiddenCandidates] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectedTalentIndex = useRef<number | null>(null);

  const { isMobileView } = useCustomMediaQuery();
  const selectedPosition = useMemo(
    () => positions.find((pos) => pos.id === selectedPositionId),
    [positions, selectedPositionId]
  );

  useEffect(() => {
    if (selectedTalent) {
      dispatch(fetchTalentByIdAsync(selectedTalent.id));
    }
  }, [selectedTalent, dispatch]);

  useEffect(() => {
    if (!selectedTalent) return;

    const refreshedSelectedTalent = talents.find(
      (item) =>
        item.id === selectedTalent.id &&
        item.match?.matchId === selectedTalent.match?.matchId
    );

    if (
      refreshedSelectedTalent &&
      refreshedSelectedTalent !== selectedTalent
    ) {
      setSelectedTalent(refreshedSelectedTalent);
    }
  }, [talents, selectedTalent]);

  useEffect(() => {
    if (!token || !companyId) return;
    dispatch(fetchPositions());
  }, [dispatch, token, companyId]);

  useEffect(() => {
    if (!selectedPositionId && positions.length > 0) {
      setSelectedPositionId(positions[0].id);
    }
  }, [positions, selectedPositionId]);

  useEffect(() => {
    if (!selectedPositionId) return;
    dispatch(
      fetchTalentsAsync({
        positionId: selectedPositionId,
        positionTitle: selectedPosition?.jobTitle,
      })
    );
  }, [dispatch, selectedPositionId, selectedPosition?.jobTitle]);

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleTitleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTitleFilter(event.target.value);
  };

  const handleSortOptionChange = (event: SelectChangeEvent) => {
    setSortOption(event.target.value);
  };

  const handlePointRangeChange = (
    _event: Event,
    newValue: number | number[]
  ) => {
    if (!Array.isArray(newValue) || newValue.length !== 2) return;
    setPointRange(newValue as number[]);
  };

  const handlePositionChange = (event: SelectChangeEvent) => {
    setSelectedPositionId(event.target.value);
    setSelectedTalent(null);
    selectedTalentIndex.current = null;
  };

  const handleShowHiddenCandidatesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowHiddenCandidates(event.target.checked);
    setSelectedTalent(null);
    selectedTalentIndex.current = null;
  };

  const handleRunBatch = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      openSnackbar("No authentication token found", "error");
      return;
    }

    try {
      setBatchRunning(true);
      const response = await axios.post(
        `${API_URL}/matching/run-batch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = response.data?.matchesCreated ?? 0;
      const updated = response.data?.matchesUpdated ?? 0;
      openSnackbar(
        `Matching batch completed. Created ${created}, updated ${updated}.`,
        "success"
      );

      if (selectedPositionId) {
        dispatch(
          fetchTalentsAsync({
            positionId: selectedPositionId,
            positionTitle: selectedPosition?.jobTitle,
          })
        );
      }
    } catch (error: any) {
      openSnackbar(
        error.response?.data?.message || error.message || "Batch run failed",
        "error"
      );
    } finally {
      setBatchRunning(false);
    }
  };

  const handleTalentClick = (talent: Talent, index: number) => {
    setSelectedTalent(talent);
    selectedTalentIndex.current = index;
  };

  const handleRequestInterview = async (selected: Talent) => {
    const token = localStorage.getItem("token");
    const matchId = selected.match?.matchId;

    if (!token || !matchId) {
      openSnackbar(t("talents.messages.matchInfoMissing"), "error");
      return;
    }

    try {
      setRequestInterviewLoading(true);
      await axios.post(
        `${API_URL}/match/requestinterview/${matchId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      openSnackbar(
        t("talents.messages.interviewRequestSent"),
        "info",
        8000
      );

      if (selectedPositionId) {
        dispatch(
          fetchTalentsAsync({
            positionId: selectedPositionId,
            positionTitle: selectedPosition?.jobTitle,
          })
        );
      }
    } catch (error: any) {
      openSnackbar(
          error.response?.data?.message ||
          error.message ||
          t("talents.messages.interviewRequestFailed"),
        "error"
      );
    } finally {
      setRequestInterviewLoading(false);
    }
  };

  const handleToggleHidden = async (selected: Talent, nextHidden: boolean) => {
    const token = localStorage.getItem("token");
    const matchId = selected.match?.matchId;

    if (!token || !matchId) {
      openSnackbar(t("talents.messages.matchInfoMissing"), "error");
      return;
    }

    try {
      setHideLoading(true);
      await axios.post(
        `${API_URL}/match/${nextHidden ? "hide" : "unhide"}/${matchId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      openSnackbar(
        nextHidden
          ? t("talents.messages.hideSuccess")
          : t("talents.messages.unhideSuccess"),
        "success"
      );

      if (selectedPositionId) {
        dispatch(
          fetchTalentsAsync({
            positionId: selectedPositionId,
            positionTitle: selectedPosition?.jobTitle,
          })
        );
      }
    } catch (error: any) {
      openSnackbar(
        error.response?.data?.message ||
          error.message ||
          (nextHidden
            ? t("talents.messages.hideFailed")
            : t("talents.messages.unhideFailed")),
        "error"
      );
    } finally {
      setHideLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedTalent(null);
    selectedTalentIndex.current = null;
  };

  useEffect(() => {
    if (selectedTalent && selectedTalentIndex.current !== null) {
      cardRefs.current[selectedTalentIndex.current]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedTalent]);

  const filteredTalents = useMemo(() => {
    return talents
      .filter((talent: Talent) =>
        showHiddenCandidates ? true : !talent.match?.isHidden
      )
      .filter((talent: Talent) =>
        statusFilter ? talent.status === statusFilter : true
      )
      .filter((talent: Talent) =>
        titleFilter
          ? talent.firstName.toLowerCase().includes(titleFilter.toLowerCase())
          : true
      )
      .filter((talent: Talent) => {
        const score = Math.round(
          talent.match?.overallScore ?? Number(talent.completeness) ?? 0
        );
        return score >= pointRange[0] && score <= pointRange[1];
      })
      .sort((a: Talent, b: Talent) => {
        if (sortOption === "name") {
          // Name A->Z: firstName, then lastName for tiebreak
          const firstNameCompare = a.firstName.localeCompare(b.firstName);
          if (firstNameCompare !== 0) return firstNameCompare;
          return (a.lastName || "").localeCompare(b.lastName || "");
        } else if (sortOption === "nameDesc") {
          // Name Z->A: firstName reverse, then lastName reverse for tiebreak
          const firstNameCompare = b.firstName.localeCompare(a.firstName);
          if (firstNameCompare !== 0) return firstNameCompare;
          return (b.lastName || "").localeCompare(a.lastName || "");
        } else if (sortOption === "score") {
          // Score high to low: match.overallScore desc, fallback to completeness
          const scoreA = Math.round(
            a.match?.overallScore ?? Number(a.completeness) ?? 0
          );
          const scoreB = Math.round(
            b.match?.overallScore ?? Number(b.completeness) ?? 0
          );
          return scoreB - scoreA; // Descending
        } else if (sortOption === "accepted") {
          // Accepted first: status priority
          const statusPriority: Record<string, number> = {
            accepted: 1,
            requested: 2,
            "": 3,
            rejected: 4,
          };
          const priorityA = statusPriority[a.status] ?? 99;
          const priorityB = statusPriority[b.status] ?? 99;
          if (priorityA !== priorityB) return priorityA - priorityB;
          // Tiebreak: by score descending
          const scoreA = Math.round(
            a.match?.overallScore ?? Number(a.completeness) ?? 0
          );
          const scoreB = Math.round(
            b.match?.overallScore ?? Number(b.completeness) ?? 0
          );
          return scoreB - scoreA;
        } else if (sortOption === "date") {
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        }
        return 0;
      });
  }, [talents, showHiddenCandidates, statusFilter, titleFilter, pointRange, sortOption]);

  if (loading || positionsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        mt: "4px",
        height: "calc(100vh - 163px)",
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: "scroll",
          transition: "margin-right 0.3s",
          width: selectedTalent ? "30%" : "100%",
        }}
      >
        {!selectedTalent && (
          <>
            <Box sx={sxProps.positionFilter}>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>{t("talents.position")}</InputLabel>
                <Select
                  value={selectedPositionId}
                  label={t("talents.position")}
                  onChange={handlePositionChange}
                  disabled={positions.length === 0}
                >
                  {positions.length === 0 ? (
                    <MenuItem value="">{t("talents.noPositions")}</MenuItem>
                  ) : (
                    positions.map((position) => (
                      <MenuItem key={position.id} value={position.id}>
                        {position.jobTitle || position.id}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHiddenCandidates}
                    onChange={handleShowHiddenCandidatesChange}
                  />
                }
                label={t("talents.showHiddenCandidates")}
                sx={{ ml: 0.5 }}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRunBatch}
                disabled={batchRunning}
              >
                {batchRunning ? t("talents.runningBatch") : t("talents.runBatch")}
              </Button>
              {selectedPosition?.jobTitle ? (
                <Typography variant="caption" color="textSecondary">
                  {t("talents.showingMatchesFor", { jobTitle: selectedPosition.jobTitle })}
                </Typography>
              ) : null}
            </Box>

            <ButtonGroup
              variant="outlined"
              aria-label="Basic button group"
              color="primary"
              sx={sxProps.viewButtons}
            >
              {isMobileView ? (
                <IconButton onClick={() => setViewMode("table")}>
                  <TableView color="primary" />
                </IconButton>
              ) : (
                <Button
                  startIcon={<TableView />}
                  onClick={() => setViewMode("table")}
                >
                  {t("talents.tableView")}
                </Button>
              )}

              {isMobileView ? (
                <IconButton
                  aria-label="card"
                  onClick={() => setViewMode("card")}
                >
                  <GridViewIcon color="primary" />
                </IconButton>
              ) : (
                <Button
                  startIcon={<GridViewIcon />}
                  onClick={() => setViewMode("card")}
                >
                  {t("talents.cardView")}
                </Button>
              )}
            </ButtonGroup>
          </>
        )}
        <FilterBar
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          titleFilter={titleFilter}
          onTitleFilterChange={handleTitleFilterChange}
          pointRange={pointRange}
          onPointRangeChange={handlePointRangeChange}
          sortOption={sortOption}
          onSortOptionChange={handleSortOptionChange}
          isSmallView={!!selectedTalent}
        />
        {selectedTalent || viewMode === "card" ? (
          <Grid
            sx={{
              bgcolor: "background.neutral",
              display: "flex",
              flexDirection: selectedTalent || isMobileView ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
            }}
            container
            columnSpacing={3}
            px={2}
          >
            {filteredTalents.map((talent: Talent, index) => (
              <Grid
                item
                key={talent.id}
                ref={(el: HTMLDivElement | null) =>
                  (cardRefs.current[index] = el)
                }
              >
                <TalentCard
                  talent={talent}
                  onClick={() => handleTalentClick(talent, index)}
                  active={
                    selectedTalent?.id === talent.id &&
                    selectedTalent?.match?.matchId === talent.match?.matchId
                  }
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid
            sx={{
              bgcolor: "background.neutral",
              display: "flex",
              alignItems: "center",
            }}
            container
            p={[2, 5]}
          >
            <TalentTable
              talents={filteredTalents}
              onRowClick={handleTalentClick}
            />
          </Grid>
        )}
      </Box>
      {!!selectedTalent && talent && (
        <TalentDetailDrawer
          open={true}
          onClose={handleCloseModal}
          talent={talent}
          positionSkillNames={(selectedPosition?.skills || []).map(
            (skill) => skill.name
          )}
          onRequestInterview={handleRequestInterview}
          requestInterviewLoading={requestInterviewLoading}
          onToggleHidden={handleToggleHidden}
          hideLoading={hideLoading}
        />
      )}
    </Box>
  );
};

export default TalentList;

const sxProps = {
  positionFilter: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    p: 2,
    bgcolor: "background.paper",
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  viewButtons: {
    position: "absolute",
    right: "20px",
    top: "30px",
    bgcolor: "white",
    zIndex: (theme: any) => theme.zIndex.appBar + 1,
    boxShadow: 3,
  },
};
